import {
  LineChart,
  LineChartProps,
} from '../../molecules/charts/chart-types/LineChart/LineChart';
import {
  AreaChart,
  AreaChartProps,
} from '../../molecules/charts/chart-types/AreaChart/AreaChart';
import {
  PieChart,
  PieChartProps,
} from '../../molecules/charts/chart-types/PieChart/PieChart';
import {
  BarChart,
  BarChartProps,
} from '../../molecules/charts/chart-types/BarChart/BarChart';
import { cn } from '../../../utils/cn';

import type { JSX } from 'react';

/**
 * Props for Pie Spark Chart
 * @template TData The type of data used in the chart
 */
interface PieSparkChartProps<TData> extends PieChartProps<TData> {
  type: 'pie';
}

/**
 * Props for Line Spark Chart
 * @template TData The type of data used in the chart
 */
interface LineSparkChartProps<TData> extends LineChartProps<TData> {
  type?: 'line';
}

/**
 * Props for Bar Spark Chart
 * @template TData The type of data used in the chart
 */
interface BarSparkChartProps<TData> extends BarChartProps<TData> {
  type: 'bar';
}

/**
 * Props for Area Spark Chart
 * @template TData The type of data used in the chart
 */
interface AreaSparkChartProps<TData> extends AreaChartProps<TData> {
  type: 'area';
}

/**
 * Union type for all possible SparkChart props
 * @template TData The type of data used in the chart
 */
export type SparkChartProps<TData> =
  | PieSparkChartProps<TData>
  | LineSparkChartProps<TData>
  | BarSparkChartProps<TData>
  | AreaSparkChartProps<TData>;

/**
 * SparkChart component that renders different types of small charts
 * @template TData The type of data used in the chart
 * @param {SparkChartProps<TData>} props - The props for the SparkChart component
 * @returns {JSX.Element} The rendered SparkChart component
 *
 * @example
 * // Line chart
 * <SparkChart type="line" data={[...]} />
 *
 * @example
 * // Area chart
 * <SparkChart type="area" data={[...]} />
 *
 * @example
 * // Pie chart
 * <SparkChart type="pie" data={[...]} />
 *
 * @example
 * // Bar chart
 * <SparkChart type="bar" data={[...]} />
 */
export const SparkChart = <TData,>(
  props: SparkChartProps<TData>,
): JSX.Element => {
  /**
   * Common props applied to all chart types
   */
  const commonProps = {
    showLegend: false,
    showTooltip: false,
    showCartesianGrid: false,
    showXAxis: false,
    showYAxis: false,
    className: cn(`h-[40px] w-full`),
    tooltipProps: { allowEscapeViewBox: { x: true, y: true } },
  };

  switch (props.type) {
    case 'area':
      return <AreaChart {...commonProps} {...props} />;
    case 'pie':
      return <PieChart {...commonProps} {...props} />;
    case 'bar':
      return <BarChart {...commonProps} {...props} />;
    case 'line':
    default:
      return <LineChart {...commonProps} {...props} />;
  }
};
